import React, { useEffect, useState } from "react";
import { Landmark, ShieldCheck, TrendingUp, Building2, Sparkles } from "lucide-react";
import api from "../../lib/api";

/**
 * Debt Intelligence panel — surfaces bureau band, CAM-Lite ratios,
 * sanction history and lender coverage on the investor-facing company
 * profile. Only appears when the founder has flipped
 * `debt_intel_consent = true` via /api/startup/debt-intelligence-consent.
 *
 * The API returns:
 *   - { consent: false, message: "..." }         ← soft empty state
 *   - { consent: true, has_data: false, ... }    ← consented but no debt yet
 *   - { consent: true, has_data: true, ... }     ← full panel
 */
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function BandPill({ band }) {
  const map = {
    Excellent: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Good: "bg-blue-100 text-blue-800 border-blue-300",
    Fair: "bg-yellow-100 text-yellow-900 border-yellow-300",
    Poor: "bg-red-100 text-red-800 border-red-300",
  };
  const cls = map[band] || "bg-ink/5 text-ink-muted border-ink/10";
  return <span className={`text-[10px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>{band || "—"}</span>;
}

export default function DebtIntelligencePanel({ startupId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/marketplace/startup/${startupId}/debt-intelligence`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [startupId]);

  if (loading) return null;

  // Silent hide when the endpoint errors — matches DebtSuitabilityTile UX.
  if (!data) return null;

  // Soft "not shared" state — do NOT expose that the company has never
  // approached us for debt; keep the message deliberately neutral.
  if (!data.consent) {
    return (
      <div
        className="border border-white/10 bg-white/70 backdrop-blur p-6 mb-6"
        data-testid="debt-intel-not-shared"
      >
        <div className="flex items-center gap-2 mb-2">
          <Landmark size={14} className="text-ink-muted" />
          <div className="overline">Debt Intelligence</div>
        </div>
        <div className="text-ink-muted text-sm leading-relaxed">
          {data.message || "This company has not shared their debt intelligence."}
        </div>
      </div>
    );
  }

  // Consented but no CRM data yet.
  if (!data.has_data) {
    return (
      <div
        className="border border-white/10 bg-white/70 backdrop-blur p-6 mb-6"
        data-testid="debt-intel-empty"
      >
        <div className="flex items-center gap-2 mb-2">
          <Landmark size={14} className="text-gold" />
          <div className="overline">Debt Intelligence</div>
          <span className="text-[10px] font-mono text-ink-muted">shared by founder</span>
        </div>
        <div className="text-ink-muted text-sm">
          No debt activity recorded yet on VenturaZ.
        </div>
      </div>
    );
  }

  const bureau = data.bureau;
  const cam = data.cam_lite;
  const totals = data.totals || {};
  const lenders = data.lender_coverage || {};

  return (
    <div
      className="border border-gold/40 bg-white/85 backdrop-blur p-6 mb-6"
      data-testid="debt-intel-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Landmark size={14} className="text-gold" />
        <div className="overline">Debt Intelligence · Verified on VenturaZ</div>
        <ShieldCheck size={12} className="text-emerald-600" />
        <span className="text-[10px] font-mono text-ink-muted">shared by founder</span>
      </div>

      {/* Row 1 — Bureau + CAM verdict */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="border border-ink/10 rounded p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Bureau</div>
          {bureau ? (
            <>
              <div className="flex items-center gap-2">
                <BandPill band={bureau.band} />
                <span className="text-[10px] text-ink-muted font-mono">{bureau.bureau}</span>
              </div>
              {bureau.pulled_at && (
                <div className="text-[10px] text-ink-muted mt-1">pulled {bureau.pulled_at.slice(0, 10)}</div>
              )}
            </>
          ) : (
            <span className="text-sm italic text-ink-muted">Not pulled</span>
          )}
        </div>
        <div className="border border-ink/10 rounded p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Credit assessment</div>
          {cam ? (
            <div className="text-sm font-heading text-navy">{cam.verdict || "Assessed"}</div>
          ) : (
            <span className="text-sm italic text-ink-muted">Not assessed</span>
          )}
        </div>
        <div className="border border-ink/10 rounded p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Cases on VenturaZ</div>
          <div className="text-lg font-heading text-navy">{totals.case_count || 0}</div>
        </div>
      </div>

      {/* Row 2 — CAM ratios */}
      {cam && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <RatioTile label="DSCR" value={cam.dscr} />
          <RatioTile label="Leverage" value={cam.leverage_ratio} suffix="×" />
          <RatioTile label="EBITDA margin" value={cam.ebitda_margin_pct} suffix="%" />
          <RatioTile label="Cash cover" value={cam.cash_cover_months} suffix=" mo" />
        </div>
      )}

      {/* Row 3 — Sanction history */}
      {(data.sanction_history || []).length > 0 ? (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2 flex items-center gap-1">
            <TrendingUp size={10} /> Sanction & disbursement history · {inr(totals.sanctioned_inr)} sanctioned · {inr(totals.disbursed_inr)} disbursed
          </div>
          <div className="border border-ink/10 rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-ink/[0.03] text-ink-soft">
                <tr>
                  <th className="text-left px-3 py-2">Lender</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Sanction</th>
                  <th className="text-right px-3 py-2">ROI · Tenure</th>
                  <th className="text-left px-3 py-2">Stage</th>
                </tr>
              </thead>
              <tbody>
                {data.sanction_history.map((la, i) => (
                  <tr key={i} className="border-t border-ink/5" data-testid={`debt-intel-lender-${i}`}>
                    <td className="px-3 py-2 font-heading text-navy">{la.lender_name || "—"}</td>
                    <td className="px-3 py-2">{la.product_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{la.sanction_amount_inr ? inr(la.sanction_amount_inr) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-[10px]">
                      {la.interest_rate_pa ? `${la.interest_rate_pa}%` : "—"} · {la.tenure_months ? `${la.tenure_months}m` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-heading uppercase px-1.5 py-0.5 rounded ${
                        la.stage === "Disbursed" ? "bg-emerald-100 text-emerald-800"
                        : la.stage === "Sanctioned" ? "bg-blue-100 text-blue-800"
                        : "bg-red-100 text-red-800"
                      }`}>{la.stage}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-xs italic text-ink-muted">No sanctions on record yet.</div>
      )}

      {/* Row 4 — Lender coverage */}
      {lenders.count > 0 && (
        <div className="mt-4 flex flex-wrap gap-1 items-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1">
            <Building2 size={10} /> Lender coverage · {lenders.count}
          </div>
          {lenders.lenders.map((l) => (
            <span key={l} className="text-[10px] font-mono px-2 py-0.5 rounded bg-navy/5 text-ink-soft" data-testid={`debt-intel-coverage-${l.replace(/\s+/g, '_')}`}>{l}</span>
          ))}
        </div>
      )}

      <div className="mt-4 text-[10px] font-mono text-ink-muted flex items-center gap-1">
        <Sparkles size={10} /> Generated from live Debt CRM · consent granted {data.consent_at?.slice(0, 10)}
      </div>
    </div>
  );
}

function RatioTile({ label, value, suffix }) {
  const has = value != null && value !== "";
  return (
    <div className="border border-ink/10 rounded p-3 bg-navy/[0.02]">
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">{label}</div>
      <div className="text-sm font-heading text-navy">
        {has ? `${Number(value).toFixed(2)}${suffix || ""}` : "—"}
      </div>
    </div>
  );
}
